import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteAllChats, deleteChat, fetchChats, renameChat } from "../api/chatHistory";
import { PaginatedChatsResponse } from "./constants";

export const usePaginatedChats = ({
  userId,
  limit = 20,
  query = null,
}: {
  userId: string;
  limit?: number;
  query?: string | null;
}) => {
  const queryKey = query ? ["chats", query] : ["chats"];

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchChats({ userId, offset: pageParam, limit, query }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const { pagination } = lastPage;
      const nextOffset = allPages.length * limit;
      return pagination.currentPage < pagination.totalPages
        ? nextOffset
        : undefined;
    },
    enabled: !!userId,
    staleTime: query ? 0 : 3 * 60 * 60 * 1000, // cache for 3 hours
  });
};

// forcefully delete cached data
export const useDeleteAllChatsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      deleteAllChats({ user_id: userId }),
    onMutate: async () => {
      const previousChats = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);

      // optimistically update cache before mutation completes
      queryClient.setQueryData(["chats"], (oldData: PaginatedChatsResponse | undefined) => {
        if (!oldData) return oldData;

        const clearedPages = oldData.pages.map(page => ({
          ...page,
          records: [], // <- Clear all records in each page
        }));

        return { ...oldData, pages: clearedPages };
      });

      return { previousChats };
    },
    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(["chats"], context.previousChats);
      }
    },
  });
};

export const useDeleteChatMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, userId }: { sessionId: string; userId: string }) =>
      deleteChat({ session_id: sessionId, user_id: userId }),
    onMutate: async (variables) => {
      const { sessionId } = variables;
      const previousChats = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);

      // optimistically update cache before mutation completes
      queryClient.setQueryData(["chats"], (oldData: PaginatedChatsResponse | undefined) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map(page => ({
          ...page,
          records: page.records.filter(chat => chat.sessionId !== sessionId),
        }));

        return { ...oldData, pages: updatedPages };
      });
      return { previousChats };
    },

    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(["chats"], context.previousChats);
      }
    },
  });
};

export const useRenameChatMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ title, sessionId }: { title: string; sessionId: string }) =>
      renameChat({ title, session_id: sessionId }),
    onMutate: async (variables) => {
      const { title, sessionId } = variables;
      const previousChats = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);

      // optimistically update cache before mutation completes
      queryClient.setQueryData(["chats"], (oldData: PaginatedChatsResponse | undefined) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map(page => ({
          ...page,
          records: page.records.map(chat =>
            chat.sessionId === sessionId
              ? { ...chat, title }
              : chat,
          ),
        }));

        return { ...oldData, pages: updatedPages };
      });
      return { previousChats };
    },

    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(["chats"], context.previousChats);
      }
    },
  });
};
